import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines, Reveal, FadeIn } from "../components/site/Reveal";
import {
  IMAGES,
  JOURNEY_THEMES,
  JOURNEY_INCLUDES,
  JOURNEY_AUDIENCES,
} from "../data/content";
import { ArrowRight } from "lucide-react";

const JourneyHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-[92vh] flex items-end overflow-hidden bg-ink">
      <motion.div style={{ scale }} className="absolute inset-0">
        <img
          src={IMAGES.cloisterLight}
          alt="Sunlight falling through an ancient stone cloister"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
      </motion.div>

      <div className="relative yadid-container pb-20 md:pb-28 pt-40">
        <FadeIn delay={0.2}>
          <p className="eyebrow text-brass mb-8">Another Practice of Yadid Advisory</p>
        </FadeIn>
        <MaskedLines
          tag="h1"
          lines={["Yadid", "Journeys"]}
          delay={0.3}
          className="font-serif text-ivory text-6xl sm:text-7xl lg:text-[9rem] leading-[0.92] tracking-[-0.03em]"
        />
        <Reveal delay={0.7} className="mt-10 max-w-2xl">
          <p className="font-serif text-2xl md:text-3xl text-brass italic">
            Immersive journeys in history, faith, culture, and relationship.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default function Journeys() {
  return (
    <div data-testid="journeys-page" className="bg-ink text-ivory">
      <JourneyHero />

      {/* Intro */}
      <section className="yadid-container py-24 md:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.06] tracking-[-0.02em] text-ivory">
                Some things cannot be understood from a conference room.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.1}>
              <p className="text-lg text-ivory/70 font-light leading-relaxed">
                Sometimes leaders need to encounter the place. Hear the story. Meet the people.
                Stand inside the history.
              </p>
              <p className="mt-6 text-lg text-ivory/70 font-light leading-relaxed">
                Yadid Journeys designs immersive experiences that deepen understanding through
                history, culture, faith, place, and relationship. Each journey is intentionally
                constructed around questions worth exploring. Not simply:{" "}
                <span className="italic text-ivory">What should we see?</span> But:{" "}
                <span className="italic text-ivory">
                  What should we understand differently when we return?
                </span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* More than travel */}
      <section className="border-y border-white/10">
        <div className="yadid-container py-24 md:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <FadeIn>
                <div className="relative h-[360px] md:h-[500px] overflow-hidden">
                  <img
                    src={IMAGES.jerusalemWindow}
                    alt="A view of an ancient city framed through weathered stone"
                    className="w-full h-full object-cover"
                  />
                </div>
              </FadeIn>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal>
                <p className="eyebrow text-brass mb-8">More Than Travel</p>
                <h2 className="font-serif text-4xl md:text-5xl text-ivory tracking-[-0.02em]">
                  A journey should change the way you see.
                </h2>
                <p className="mt-8 text-ivory/60 font-light">A Yadid Journey may include:</p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {JOURNEY_INCLUDES.map((item) => (
                    <li key={item} className="flex gap-3 items-baseline text-ivory/80 font-light">
                      <span className="text-brass text-xs">&#9679;</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-8 font-serif text-2xl text-brass italic">
                  Travel is the medium. Understanding is the purpose.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Themes */}
      <section className="yadid-container py-24 md:py-36">
        <Reveal>
          <p className="eyebrow text-brass mb-6">Themes</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ivory tracking-[-0.02em] max-w-3xl">
            Potential journeys may explore:
          </h2>
        </Reveal>
        <div className="mt-16 space-y-px">
          {JOURNEY_THEMES.map((t, i) => (
            <Reveal key={t.name} delay={(i % 2) * 0.06}>
              <Link
                to={`/journeys/${t.slug}`}
                data-testid={`journey-theme-${t.slug}`}
                className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center py-10 border-t border-white/10 transition-colors duration-500 hover:bg-white/[0.03]"
              >
                <div className="md:col-span-3 relative h-40 md:h-48 overflow-hidden">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  />
                </div>
                <div className="md:col-span-2">
                  <h3 className="font-serif text-3xl md:text-4xl text-ivory">{t.name}</h3>
                </div>
                <div className="md:col-span-5 md:col-start-7">
                  <p className="text-ivory/65 font-light leading-relaxed">{t.body}</p>
                </div>
                <div className="md:col-span-1 flex md:justify-end">
                  <ArrowRight
                    className="text-brass opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500"
                    size={24}
                  />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Who journeys are for */}
      <section className="border-t border-white/10">
        <div className="yadid-container py-24 md:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="eyebrow text-brass mb-8">Who Journeys Are For</p>
                <h2 className="font-serif text-4xl md:text-5xl text-ivory tracking-[-0.02em] leading-[1.08]">
                  Designed around the people participating.
                </h2>
                <p className="mt-8 text-ivory/60 font-light leading-relaxed max-w-md">
                  Every journey is designed around the people participating and the understanding
                  they hope to develop.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <ul className="border-t border-white/10">
                {JOURNEY_AUDIENCES.map((a, i) => (
                  <Reveal key={a} delay={i * 0.04}>
                    <li className="py-5 border-b border-white/10 flex items-center justify-between">
                      <span className="font-serif text-2xl text-ivory">{a}</span>
                      <span className="eyebrow text-brass/60">{String(i + 1).padStart(2, "0")}</span>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="relative overflow-hidden border-t border-white/10">
        <div className="absolute inset-0 opacity-25">
          <img src={IMAGES.jerusalemStair} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-ink/60" />
        </div>
        <div className="relative yadid-container py-28 md:py-44 text-center flex flex-col items-center">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-5xl lg:text-[3.9rem] leading-[1.05] tracking-[-0.02em] text-ivory">
              Go somewhere worth understanding.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-14">
              <Link
                to="/contact"
                data-testid="journeys-closing-cta"
                className="inline-flex items-center gap-3 border border-brass text-brass px-8 py-4 text-[0.78rem] tracking-[0.16em] uppercase font-semibold transition-all duration-500 hover:bg-brass hover:text-ink"
              >
                Begin a Conversation About a Journey <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
